import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const Additional = () => {
  const [markdownContent, setMarkdownContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState('embedded_SIM.md');

  const markdownCategories = [
    {
      category: 'Basic Understanding',
      files: [
        { file: 'embedded_SIM.md', title: 'What does eSIM stand for?' },
        { file: 'eSIM_definition.md', title: 'What is an eSIM?' },
        { file: 'differences.md', title: 'eSIM vs Physical SIM differences' },
        { file: 'do_I_need_an_eSIM.md', title: 'Do I need an eSIM?' }
      ]
    },
    {
      category: 'Device Compatibility',
      files: [
        { file: 'phone_esim_compatibility.md', title: 'Is my phone eSIM compatible?' },
        { file: 'phones_supporting_esim.md', title: 'Which phones support eSIM?' },
        { file: 'smartwatch_esim_support.md', title: 'Do smartwatches support eSIM?' },
        { file: 'phone_unlocked_esim.md', title: 'Is my phone unlocked for eSIM?' }
      ]
    },
    {
      category: 'Installation & Activation',
      files: [
        { file: 'how_to_install_esim.md', title: 'How to install eSIM' },
        { file: 'scan_qr_code_activation.md', title: 'How to scan QR code' },
        { file: 'when_does_esim_activate.md', title: 'When does eSIM activate?' },
        { file: 'install_esim_before_traveling.md', title: 'Install eSIM before travel?' }
      ]
    },
    {
      category: 'Travel & International Use',
      files: [
        { file: 'esim_international_travel.md', title: 'eSIM for international travel' },
        { file: 'travel_data_plan_start.md', title: 'When does travel plan start?' },
        { file: 'avoid_roaming_charges_esim.md', title: 'Avoid roaming charges' },
        { file: 'receive_texts_with_esim.md', title: 'Receive texts with eSIM?' }
      ]
    },
    {
      category: 'Data Plans & Usage',
      files: [
        { file: 'types_of_esim_plans.md', title: 'Types of eSIM plans' },
        { file: 'how_much_data_needed.md', title: 'How much data needed?' },
        { file: 'hotspot_tethering_esim.md', title: 'Hotspot/tethering with eSIM' },
        { file: 'esim_plan_duration.md', title: 'How long do plans last?' },
        { file: 'throttling_unlimited_plans.md', title: 'Throttling on unlimited plans' }
      ]
    },
    {
      category: 'Multiple SIMs & Dual SIM',
      files: [
        { file: 'dual_sim_esim_physical.md', title: 'Use eSIM and physical SIM together' },
        { file: 'multiple_esims_one_device.md', title: 'Multiple eSIMs on one device' },
        { file: 'switch_between_sims.md', title: 'Switch between SIMs' },
        { file: 'esim_profile_capacity.md', title: 'How many eSIM profiles fit?' }
      ]
    },
    {
      category: 'Technical Issues & Troubleshooting',
      files: [
        { file: 'esim_not_working.md', title: 'Why is my eSIM not working?' },
        { file: 'esim_stuck_activating.md', title: 'eSIM stuck on "Activating"' },
        { file: 'esim_no_service.md', title: 'Why "No Service" with eSIM?' },
        { file: 'esim_connectivity_issues.md', title: 'Fix eSIM connectivity issues' },
        { file: 'esim_restart_reset.md', title: 'Restart/reset eSIM' },
        { file: 'esim_apn_settings.md', title: 'Update APN settings' }
      ]
    },
    {
      category: 'Network & Speed',
      files: [
        { file: 'esim_4g_5g_speeds.md', title: 'Will I get 4G/5G with eSIM?' },
        { file: 'esim_vs_physical_sim_speeds.md', title: 'eSIM vs physical SIM speeds' },
        { file: 'esim_network_connection.md', title: 'Which network will eSIM connect to?' }
      ]
    },
    {
      category: 'Security & Safety',
      files: [
        { file: 'esim_security_vs_physical.md', title: 'Are eSIMs more secure?' },
        { file: 'esim_theft_protection.md', title: 'Can eSIMs be stolen?' },
        { file: 'lost_phone_esim.md', title: 'Lost phone with eSIM?' }
      ]
    },
    {
      category: 'Switching Devices & Transfers',
      files: [
        { file: 'esim_transfer_new_phone.md', title: 'Transfer eSIM to new phone' },
        { file: 'esim_switch_between_devices.md', title: 'Switch eSIM between devices' },
        { file: 'esim_delete_during_activation.md', title: 'Delete eSIM during activation' }
      ]
    },
    {
      category: 'Cost & Refunds',
      files: [
        { file: 'esim_refund_policy.md', title: 'Get refund if eSIM doesn\'t work?' },
        { file: 'esim_cost_comparison.md', title: 'eSIM costs vs alternatives' },
        { file: 'esim_vs_roaming_costs.md', title: 'eSIM vs roaming costs' },
        { file: 'esim_vs_local_sim_costs.md', title: 'eSIM vs local SIM costs' },
        { file: 'esim_short_vs_long_trips.md', title: 'eSIM for short vs long trips' }
      ]
    }
  ];

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`/markdown/${selectedFile}`);
        if (!res.ok) throw new Error('Failed to fetch markdown');
        setMarkdownContent(await res.text());
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedFile]);

  if (loading) return <div className="flex justify-center items-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  if (error) return <div className="text-red-600 text-center p-8">Error: {error}</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">eSIM FAQ</h1>
          <p className="text-lg text-gray-600">Everything you need to know about eSIM technology</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Navigation Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-8 max-h-[calc(100vh-8rem)] overflow-y-auto">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Topics</h2>
              <nav className="space-y-4">
                {markdownCategories.map((category) => (
                  <div key={category.category}>
                    <h3 className="text-sm font-medium text-gray-900 mb-2 border-b border-gray-200 pb-1">
                      {category.category}
                    </h3>
                    <div className="space-y-1 mb-4">
                      {category.files.map((item) => (
                        <button
                          key={item.file}
                          onClick={() => setSelectedFile(item.file)}
                          className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                            selectedFile === item.file
                              ? 'bg-blue-100 text-blue-900 font-medium'
                              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                          }`}
                        >
                          {item.title}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </nav>
            </div>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-3">
            <article className="prose prose-zinc dark:prose-invert max-w-none bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {markdownContent}
              </ReactMarkdown>
            </article>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Additional;